/* eslint-disable new-cap */
import type { ColumnDef, Row } from "@tanstack/react-table";
import type { ExecutionResult } from "graphql";
import _ from "lodash";

import {
  AcceptanceFormatter,
  LocationFormatter,
  OriginFormatter,
  PriorityFormatter,
  SeverityFormatter,
  SpecificFormatter,
  StatusFormatter,
  TagsFormatter,
  TechniqueFormatter,
  TreatmentAssignedFormatter,
  TreatmentFormatterCell,
  VerificationFormatter,
  ZeroRiskFormatter,
} from "./formatters";
import type { IFindingsPriorityResponse } from "./hooks/types";
import type { ICloseVulnerabilitiesResultAttr } from "./types";

import type { IVulnDataAttr } from "features/vulnerabilities/handle-acceptance-modal/types";
import type { IVulnRowAttr } from "features/vulnerabilities/types";
import type { VulnerabilityStateReason } from "gql/graphql";
import type { ICVSSVersionObject } from "hooks/use-settings-store";
import { filterDate } from "utils/date";
import { translate } from "utils/translations/translate";

type TGroup = IFindingsPriorityResponse["data"]["group"];

const formatVulnsWithAcceptance = (vulns: IVulnRowAttr[]): IVulnRowAttr[] =>
  vulns.map((vuln): IVulnRowAttr => ({ ...vuln, acceptance: "APPROVED" }));

const getRequestedZeroRiskVulns = (
  vulnerabilities: IVulnRowAttr[],
): IVulnDataAttr[] =>
  vulnerabilities.reduce(
    (
      requestedZeroRiskVulns: IVulnDataAttr[],
      vuln: IVulnRowAttr,
    ): IVulnDataAttr[] => {
      return vuln.zeroRisk === "Requested"
        ? [...requestedZeroRiskVulns, { acceptance: "", ...vuln }]
        : requestedZeroRiskVulns;
    },
    [],
  );

const getSubmittedVulns = (vulnerabilities: IVulnRowAttr[]): IVulnRowAttr[] =>
  vulnerabilities.reduce(
    (submittedVulns: IVulnRowAttr[], vuln: IVulnRowAttr): IVulnRowAttr[] => {
      return vuln.state === "SUBMITTED"
        ? [...submittedVulns, { acceptance: "", ...vuln }]
        : submittedVulns;
    },
    [],
  );

const formatPriority = (
  priority?: number,
  groupPriority?: number,
): number | undefined =>
  priority === undefined
    ? undefined
    : Math.round((priority * (groupPriority ?? 0)) / 100);

const filterVerification = (
  row: IVulnRowAttr,
  value: string | undefined,
): boolean => {
  if (value === "" || value === undefined) return true;

  const currentVerification = String(row.verification);
  if (value === "NotRequested") {
    return (
      row.state === "VULNERABLE" &&
      !_.includes(["On_hold", "Requested"], currentVerification)
    );
  }

  return currentVerification.toLowerCase().includes(value.toLowerCase());
};

const isRejectedOrSafeOrSubmitted = (row: Row<IVulnRowAttr>): boolean =>
  ["REJECTED", "SAFE", "SUBMITTED"].includes(row.original.state);

const isVerificationRequestedOrOnHold = (row: Row<IVulnRowAttr>): boolean =>
  ["requested", "on_hold"].includes(
    (row.original.verification ?? "").toLowerCase(),
  );

const isVerificationNotRequested = (row: Row<IVulnRowAttr>): boolean =>
  row.original.verification?.toLowerCase() !== "requested";

const isNotRejected = (row: Row<IVulnRowAttr>): boolean =>
  row.original.state !== "REJECTED";

const isNotVulnerable = (row: Row<IVulnRowAttr>): boolean =>
  row.original.state !== "VULNERABLE";

const isSafe = (row: Row<IVulnRowAttr>): boolean =>
  row.original.state === "SAFE";

type TCloseVulnerabilitiesResult =
  ExecutionResult<ICloseVulnerabilitiesResultAttr>;

const handleCloseVulnerabilitiesAux = async (
  closeVulnerabilities: (
    variables: Record<string, unknown>,
  ) => Promise<TCloseVulnerabilitiesResult>,
  findingId: string,
  justification: VulnerabilityStateReason,
  vulnerabilities: string[],
): Promise<TCloseVulnerabilitiesResult[]> => {
  const chunkSize = 64;
  const vulnChunks = _.chunk(vulnerabilities, chunkSize);
  const closedChunks = vulnChunks.map(
    (chunk): (() => Promise<TCloseVulnerabilitiesResult[]>) =>
      async (): Promise<TCloseVulnerabilitiesResult[]> =>
        Promise.all([
          closeVulnerabilities({
            variables: {
              findingId,
              justification,
              vulnerabilities: chunk,
            },
          }),
        ]),
  );

  return closedChunks.reduce(
    async (
      previousValue,
      currentValue,
    ): Promise<TCloseVulnerabilitiesResult[]> => [
      ...(await previousValue),
      ...(await currentValue()),
    ],
    Promise.resolve<TCloseVulnerabilitiesResult[]>([]),
  );
};

const handleCloseVulnerabilities = async (
  closeVulnerabilities: (
    variables: Record<string, unknown>,
  ) => Promise<TCloseVulnerabilitiesResult>,
  justification: VulnerabilityStateReason,
  vulnerabilities: IVulnRowAttr[],
): Promise<TCloseVulnerabilitiesResult[][]> => {
  const vulnerabilitiesByFinding = _.groupBy(
    vulnerabilities,
    (vuln: IVulnRowAttr): string => vuln.findingId,
  );
  const closedChunks = Object.entries(vulnerabilitiesByFinding).map(
    ([findingId, chunkedVulnerabilities]: [
      string,
      IVulnRowAttr[],
    ]): (() => Promise<TCloseVulnerabilitiesResult[][]>) =>
      async (): Promise<TCloseVulnerabilitiesResult[][]> => {
        return Promise.all([
          handleCloseVulnerabilitiesAux(
            closeVulnerabilities,
            findingId,
            justification,
            chunkedVulnerabilities.map((vuln): string => vuln.id),
          ),
        ]);
      },
  );

  return closedChunks.reduce(
    async (
      previousValue,
      currentValue,
    ): Promise<TCloseVulnerabilitiesResult[][]> => [
      ...(await previousValue),
      ...(await currentValue()),
    ],
    Promise.resolve<TCloseVulnerabilitiesResult[][]>([]),
  );
};

const getColumns = (
  group: TGroup,
  cvssVersion: ICVSSVersionObject,
  isAdvanced: boolean,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): ColumnDef<IVulnRowAttr, any>[] => [
  {
    columns: [
      {
        accessorKey: "where",
        cell: LocationFormatter,
        header: translate.t("searchFindings.tabVuln.vulnTable.where"),
        maxSize: 800,
        minSize: 200,
      },
      {
        accessorKey: "source",
        cell: OriginFormatter,
        header: translate.t("searchFindings.tabVuln.vulnTable.origin"),
        id: "origin",
      },
      {
        accessorKey: "specific",
        cell: SpecificFormatter,
        header: translate.t("searchFindings.tabVuln.vulnTable.specific"),
        sortingFn: "alphanumeric",
      },
      {
        accessorKey: "state",
        cell: StatusFormatter,
        header: translate.t("searchFindings.tabVuln.vulnTable.status"),
      },
    ],
    header: "Vulnerability information",
  },
  {
    columns: [
      {
        accessorKey: cvssVersion.scoreKey,
        cell: SeverityFormatter,
        header: cvssVersion.header,
        id: "CVSSF_V4_SCORE",
        minSize: 150,
      },
      {
        accessorKey: "priority",
        cell: (cell): JSX.Element =>
          PriorityFormatter(cell, group?.totalOpenPriority ?? 0),
        header: translate.t("searchFindings.tabVuln.vulnTable.priority"),
        id: "priority_score",
        minSize: 150,
        sortUndefined: "last",
      },
    ],
    header: "Risk information",
  },
  {
    columns: [
      {
        accessorKey: "reportDate",
        filterFn: filterDate,
        header: translate.t("searchFindings.tabVuln.vulnTable.reportDate"),
        id: "REPORT_DATE",
        minSize: 170,
      },
    ],
    header: "Report date",
  },
  {
    columns: [
      {
        accessorKey: "technique",
        cell: (cell): JSX.Element => TechniqueFormatter(cell, isAdvanced),
        header: translate.t("searchFindings.tabVuln.vulnTable.technique"),
      },
    ],
    header: "Technique",
  },
  {
    columns: [
      {
        accessorKey: "treatmentStatus",
        cell: TreatmentFormatterCell,
        header: translate.t("searchFindings.tabVuln.vulnTable.treatment"),
        id: "TREATMENT_STATUS",
        minSize: 200,
      },
      {
        accessorKey: "treatmentAcceptanceStatus",
        cell: AcceptanceFormatter,
        header: translate.t(
          "searchFindings.tabVuln.vulnTable.treatmentAcceptance",
        ),
        id: "TREATMENT_ACCEPTANCE_STATUS",
        minSize: 250,
      },
      {
        accessorKey: "verification",
        cell: VerificationFormatter,
        header: translate.t("searchFindings.tabVuln.vulnTable.reattack"),
      },
      {
        accessorKey: "tag",
        cell: TagsFormatter,
        enableSorting: false,
        header: translate.t("searchFindings.tabVuln.vulnTable.tags"),
      },
      {
        accessorKey: "zeroRisk",
        cell: ZeroRiskFormatter,
        header: translate.t("searchFindings.tabVuln.vulnTable.zeroRisk"),
        id: "ZERO_RISK",
      },
      {
        accessorKey: "treatmentAssigned",
        cell: TreatmentAssignedFormatter,
        header: translate.t(
          "searchFindings.tabVuln.vulnTable.treatmentAssigned",
        ),
        id: "TREATMENT_ASSIGNED",
        minSize: 200,
      },
    ],
    header: "Vulnerability management",
  },
];

export {
  filterVerification,
  formatPriority,
  getColumns,
  getRequestedZeroRiskVulns,
  getSubmittedVulns,
  formatVulnsWithAcceptance,
  handleCloseVulnerabilities,
  isNotRejected,
  isSafe,
  isNotVulnerable,
  isRejectedOrSafeOrSubmitted,
  isVerificationNotRequested,
  isVerificationRequestedOrOnHold,
};
